//#region src/sanitizer.ts
/**
* HIPAA-compliant metadata sanitizer for Statsig events.
*
* Any event metadata passed through `sanitizeMetadata` has:
* - Keys matching known PHI field names redacted to `[REDACTED - PHI field]`.
* - Values matching obvious PHI patterns (SSN, email) redacted.
* - URLs/paths with patient identifiers stripped.
* - Person-name patterns in title/label/name values redacted.
*
* The blocklist is comprehensive by default. Apps may extend it via `createSanitizer`
* but should never subtract from it — compliance risk flows one way.
*/
/**
* Comprehensive list of PHI field names that must NEVER be included in analytics events.
* Covers HIPAA identifiers and common clinical/administrative fields across
* SmartScribe apps (provider-side notes, patient-side forms, EHR integrations).
*/
const DEFAULT_PHI_FIELDS = [
	"patient_name",
	"patientName",
	"name",
	"firstName",
	"first_name",
	"lastName",
	"last_name",
	"middleName",
	"middle_name",
	"suffix",
	"prefix",
	"maiden_name",
	"maidenName",
	"full_name",
	"fullName",
	"display_name",
	"displayName",
	"email",
	"patient_email",
	"patientEmail",
	"phone",
	"phone_number",
	"phoneNumber",
	"patient_phone",
	"patientPhone",
	"mobile",
	"cell",
	"home_phone",
	"work_phone",
	"fax",
	"fax_number",
	"address",
	"patient_address",
	"patientAddress",
	"street",
	"street_address",
	"streetAddress",
	"address_line_1",
	"address_line_2",
	"addressLine1",
	"addressLine2",
	"city",
	"state",
	"zip",
	"zip_code",
	"zipCode",
	"postal_code",
	"country",
	"ssn",
	"social_security_number",
	"socialSecurityNumber",
	"dob",
	"date_of_birth",
	"dateOfBirth",
	"birthdate",
	"birth_date",
	"birthDate",
	"age",
	"mrn",
	"medical_record_number",
	"medicalRecordNumber",
	"patient_id_external",
	"external_id",
	"externalId",
	"drivers_license",
	"license_number",
	"diagnosis",
	"diagnoses",
	"icd10_codes",
	"icd_codes",
	"diagnosis_codes",
	"chief_complaint",
	"chiefComplaint",
	"presenting_problem",
	"reason_for_visit",
	"symptoms",
	"allergies",
	"medications",
	"medication",
	"prescriptions",
	"treatment",
	"treatments",
	"procedures",
	"surgeries",
	"medical_history",
	"medicalHistory",
	"family_history",
	"social_history",
	"past_medical_history",
	"surgical_history",
	"lab_results",
	"test_results",
	"vitals",
	"vital_signs",
	"blood_pressure",
	"pulse",
	"temperature",
	"weight",
	"height",
	"bmi",
	"exam_findings",
	"physical_exam",
	"assessment",
	"plan",
	"prognosis",
	"hpi",
	"history_of_present_illness",
	"ros",
	"review_of_systems",
	"note_content",
	"noteContent",
	"note_text",
	"noteText",
	"content",
	"text",
	"transcription",
	"transcription_text",
	"transcriptionText",
	"audio_transcript",
	"transcript",
	"summary",
	"clinical_notes",
	"provider_notes",
	"nursing_notes",
	"progress_notes",
	"insurance_id",
	"insuranceId",
	"member_id",
	"memberId",
	"subscriber_id",
	"subscriberId",
	"group_number",
	"groupNumber",
	"policy_number",
	"policyNumber",
	"insurance_plan",
	"plan_name",
	"insurance_provider",
	"payer_name",
	"form_title",
	"form_content",
	"form_response",
	"form_responses",
	"response",
	"responses",
	"answer",
	"answers",
	"comments",
	"notes",
	"free_text",
	"freeText",
	"description",
	"details",
	"instructions",
	"special_instructions",
	"message",
	"body",
	"subject",
	"title",
	"label",
	"value",
	"custom_field",
	"custom_fields",
	"provider_name",
	"providerName",
	"physician_name",
	"doctor_name",
	"referring_provider",
	"referringProvider",
	"npi",
	"provider_npi",
	"dea",
	"license",
	"emergency_contact",
	"emergency_contact_name",
	"emergency_contact_phone",
	"guardian_name",
	"guarantor_name",
	"file_path",
	"file_name",
	"document_url",
	"attachment_url",
	"recording_url",
	"audio_url",
	"appointment_notes",
	"visit_reason",
	"appointment_type_details"
];
const DEFAULT_PHI_FIELDS_SET = new Set(DEFAULT_PHI_FIELDS.map((f) => f.toLowerCase()));
const PHI_PREFIXES = [
	"patient_name",
	"provider_name",
	"physician_name",
	"doctor_name",
	"referring_provider",
	"emergency_contact_name"
];
const PHI_SUBSTRINGS = [
	"email",
	"phone",
	"address",
	"ssn",
	"dob",
	"birth",
	"diagnosis",
	"medication",
	"treatment",
	"prescription",
	"allerg",
	"insurance",
	"medical",
	"clinical",
	"symptom",
	"complaint",
	"history",
	"transcript",
	"comment",
	"description"
];
const PHI_EXACT_MATCHES = new Set([
	"form_title",
	"test_name",
	"appointment_type_name",
	"update_reason"
]);
const ANALYTICS_APPROVED_FIELDS = new Set([
	"template_name",
	"section_name",
	"section_type",
	"template_type"
]);
/**
* Title-Case two-word values that look like person names but are actually
* common clinical/administrative terms. Preserved in name-pattern redaction.
* Exported for reference — extend via a wrapper if an app needs more.
*/
const SAFE_NAME_TERMS = [
	"Blood Pressure",
	"Heart Rate",
	"Physical Exam",
	"Emergency Contact",
	"Lab Order",
	"Follow Up"
];
const PHI_REGEX_PATTERNS = [
	/.*member.*id/i,
	/.*claim.*detail/i,
	/.*note.*content/i,
	/.*free.*text/i,
	/form.*response/i,
	/.*answer$/i
];
/**
* Checks if a field name might contain PHI based on optimized checks.
* Performance: O(1) for exact matches, O(n) for substrings, regex only when needed.
*/
function isPotentialPHIField(fieldName) {
	const lowerField = fieldName.toLowerCase();
	if (ANALYTICS_APPROVED_FIELDS.has(lowerField)) return false;
	if (DEFAULT_PHI_FIELDS_SET.has(lowerField)) return true;
	if (PHI_EXACT_MATCHES.has(lowerField)) return true;
	for (const prefix of PHI_PREFIXES) if (lowerField.startsWith(prefix)) return true;
	for (const substring of PHI_SUBSTRINGS) if (lowerField.includes(substring)) return true;
	for (const pattern of PHI_REGEX_PATTERNS) if (pattern.test(fieldName)) return true;
	return false;
}
/**
* Checks if a value might contain PHI based on patterns.
* Safety net for SSN/email patterns; assumes callers control most input.
*/
function isPotentialPHIValue(value) {
	if (/^\d{9}$/.test(value) || /^\d{3}-\d{2}-\d{4}$/.test(value)) return true;
	if (/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/.test(value)) return true;
	if (/\b\d{10}\b/.test(value)) return true;
	return false;
}
function sanitizeValue(key, value) {
	let stringValue;
	if (typeof value === "string") stringValue = value;
	else if (typeof value === "number" || typeof value === "boolean") stringValue = String(value);
	else if (value instanceof Date) stringValue = value.toISOString();
	else try {
		stringValue = JSON.stringify(value);
	} catch {
		console.warn(`[Analytics] Could not stringify value for key: ${key}`);
		return {
			success: false,
			reason: "invalid"
		};
	}
	if (isPotentialPHIValue(stringValue)) {
		console.warn(`[Analytics] Potential PHI detected in value for key: ${key}`);
		return {
			success: false,
			reason: "phi"
		};
	}
	if (key.includes("url") || key.includes("path")) stringValue = stringValue.replace(/\/patients?\/[\w-]+/gi, "/patient/[REDACTED]").replace(/patient[_-]?id=[\w-]+/gi, "patient_id=[REDACTED]").replace(/mrn=[\w-]+/gi, "mrn=[REDACTED]");
	const lowerKey = key.toLowerCase();
	if (!ANALYTICS_APPROVED_FIELDS.has(lowerKey) && (key.includes("title") || key.includes("label") || key.includes("name"))) stringValue = stringValue.replace(/\b[A-Z][a-z]+\s+[A-Z][a-z]+\b/g, (match) => {
		return SAFE_NAME_TERMS.includes(match) ? match : "[NAME_REDACTED]";
	});
	return {
		success: true,
		value: stringValue
	};
}
/**
* Sanitizes metadata for HIPAA compliance while preserving key structure.
* Keys are preserved; values are sanitized or redacted.
*/
function sanitizeMetadata(metadata) {
	if (!metadata) return {};
	const sanitized = {};
	Object.entries(metadata).forEach(([key, value]) => {
		if (value === null || value === void 0) return;
		if (isPotentialPHIField(key)) {
			sanitized[key] = `[REDACTED - PHI field]`;
			console.warn(`[Analytics] Redacted PHI field: ${key}`);
			return;
		}
		const result = sanitizeValue(key, value);
		if (result.success) sanitized[key] = result.value;
		else switch (result.reason) {
			case "phi":
				sanitized[key] = "[REDACTED - PHI detected]";
				break;
			case "invalid":
				sanitized[key] = "[REDACTED - Invalid value]";
				break;
		}
	});
	return sanitized;
}
/**
* Validates that an event name is not accidentally including PHI.
*/
function validateEventName(eventName) {
	const lowerEventName = eventName.toLowerCase();
	for (const pattern of [
		/patient_\d+/,
		/mrn_\d+/,
		/\b\d{3}-\d{2}-\d{4}\b/,
		/\b[a-zA-Z]+_[a-zA-Z]+\b.*@/
	]) if (pattern.test(lowerEventName)) {
		console.error(`[Analytics] Event name may contain PHI: ${eventName}`);
		return false;
	}
	return true;
}
/**
* Anonymizes user IDs via SHA-256. Returns first 16 hex chars.
* Useful when tracking unique users without exposing real IDs.
*/
async function hashUserId(userId) {
	const data = new TextEncoder().encode(userId);
	const hashBuffer = await crypto.subtle.digest("SHA-256", data);
	return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, "0")).join("").substring(0, 16);
}
/**
* Sanitizes error messages to remove potential PHI.
* Use before sending error messages to analytics / Sentry events.
*/
function sanitizeErrorMessage(error) {
	if (!error) return "Unknown error";
	let message = "";
	if (error instanceof Error) message = error.message;
	else if (typeof error === "string") message = error;
	else message = "Error occurred";
	message = message.replace(/patient[_\s]+\S+/gi, "patient_[REDACTED]").replace(/\b\d{3}-\d{2}-\d{4}\b/g, "[SSN_REDACTED]").replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, "[EMAIL_REDACTED]").replace(/\b\d{10}\b/g, "[PHONE_REDACTED]");
	return message;
}
/**
* Safe metadata builders for common tracking scenarios.
*/
const createSafeMetadata = {
	timing: (duration, label) => ({
		duration_ms: String(Math.round(duration)),
		...label && { action_type: label }
	}),
	error: (error, context) => ({
		error_message: sanitizeErrorMessage(error),
		...context && { error_type: context }
	}),
	feature: (featureName, action, value) => ({
		feature: featureName,
		action_type: action,
		...value !== void 0 && { count: (() => {
			if (value === null) return void 0;
			if (typeof value === "string") return value;
			if (typeof value === "number" || typeof value === "boolean") return String(value);
			return JSON.stringify(value);
		})() }
	}),
	performance: (metric, value, unit = "ms") => ({
		metric,
		[`${metric}_${unit}`]: Math.round(value).toString()
	})
};

//#endregion
export { isPotentialPHIField as a, sanitizeMetadata as c, hashUserId as i, validateEventName as l, SAFE_NAME_TERMS as n, isPotentialPHIValue as o, createSafeMetadata as r, sanitizeErrorMessage as s, DEFAULT_PHI_FIELDS as t };
//# sourceMappingURL=sanitizer-CLnKD_rP.js.map